import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { matchPath, useRouteMatch } from 'react-router'
import Image from 'components/Common/Image'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import useOffer from 'hooks/Offers/useOffer'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import { isLabTakeoverBannerContentPayload } from 'lib/lab/dynamicPayload'
import IconButton from 'LuxKit/Button/IconButton'
import LineMultiplyIcon from 'LuxKit/Icons/line/LineMultiplyIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import { getThemeClassName } from 'lib/theme/themeUtils'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useSiteTakeoverTheme } from '../hooks/useSiteTakeoverTheme'
import { useCurrentSiteTakeoverV2 } from '../hooks/useCurrentSiteTakeover'
import { EmptyArray } from 'lib/array/arrayUtils'
import { isBannerDismissed, recordSiteBannerDismissal } from 'storage/siteBanner'
import moment from 'moment'
import { stripMarkdownFormatting } from 'lib/string/stripMarkdown'
import useResponsiveSelector from 'hooks/useResponsiveSelector'

const PopupContainer = styled.div`
  position: fixed;
  padding: ${rem(8)} ${rem(12)};
  overflow: hidden;
  border-radius: ${rem(8)};
  bottom: ${rem(10)};
  right: ${rem(12)};
  left: ${rem(12)};
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background-color: white;
  
  ${mediaQueryUp.tablet} {
    width: ${rem(343)};
    padding: ${rem(20)};
    left: unset;
  }
`

const PopupBackground = styled.div<{ backgroundStyle?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  background: ${props => props.backgroundStyle};
  z-index: 0;
`

const Content = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
`

const DismissButton = styled.button`
  padding: ${rem(4)};
  border-radius: ${rem(2)};
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`

const offerPageRoutes: Array<string> = [
  '/:regionCode/offer/:slug/:offerId',
  '/:regionCode/cruises/:offerId',
  '/:regionCode/cruises/:slug/:offerId',
  '/:regionCode/tour/:offerId',
  '/:regionCode/tour/:slug/:offerId',
  '/:regionCode/partner/:slug/:offerId',
]

const noBannerRoutes: Array<string> = [
  '/:regionCode/search',
  '/:regionCode/checkout',
  '/:regionCode/account',
  '/:regionCode/booking',
  '/:regionCode/bb/booking',
  '/:regionCode/trip-planner/generate-trip',
]

const toursRoutes: Array<string> = [
  '/:regionCode/tours',
  '/:regionCode/tour/:offerId',
]

const invalidExactRoutes: Array<string> = [
  '/:regionCode',
  '/:regionCode/triple-points',
]

const homePageRoutes: Array<string> = [
  '/:regionCode',
]

const BANNER_DISMISSAL_PERIOD_DAYS = 7

function SiteTakeoverPopup() {
  const match = useRouteMatch<{ offerId: string }>(offerPageRoutes)
  const [offer] = useOffer(match?.params.offerId)
  const currentPath = useAppSelector(state => state.router.location.pathname)
  // Call all hooks unconditionally
  const siteTakeoverV2 = useCurrentSiteTakeoverV2()
  const siteTakeover = siteTakeoverV2

  const siteTakeoverOnTourV2 = useCurrentSiteTakeoverV2({ heroVertical: 'tours' })
  const siteTakeoverOnTour = siteTakeoverOnTourV2

  const showingOfferBanner = !!(offer && siteTakeover?.offerTypes?.has(offer.type))
  const siteTakeoverTheme = useSiteTakeoverTheme()
  const enableSiteTakeoverBannerContent = useLabFeatureFlag(LabFeatureFlags.enableTakeoverBannerContent)
  const popupPayload: App.LabTakeoverBannerContentPayload | undefined = useMemo(() => {
    if (enableSiteTakeoverBannerContent?.enabled) {
      const payload = enableSiteTakeoverBannerContent.value
      if (isLabTakeoverBannerContentPayload(payload)) {
        return payload
      }
    }
    return undefined
  }, [enableSiteTakeoverBannerContent])

  // Generate a unique identifier for the popup configuration
  const popupIdentifier = useMemo(() => {
    if (popupPayload) {
      return `siteTakeoverPopup_lab_${popupPayload.title ?? 'default'}`
    }
    return 'siteTakeoverPopup_default'
  }, [popupPayload])

  const [isDismissed, setIsDismissed] = useState(false)

  // Check dismissal state on mount and when popup identifier changes
  useEffect(() => {
    setIsDismissed(isBannerDismissed(popupIdentifier))
  }, [popupIdentifier])

  const hide = useMemo((): boolean => {
    const exceptedRoutes: Array<string> = siteTakeover?.siteWide?.banner.exceptedRoutes ?? EmptyArray
    // hide on:
    // - The home page (has its own banner)
    // - Search page results (has it's own banner)
    // - Would show the offer page banner (has it's own banner)
    // - Any of the other no banner routes (shouldn't show banners)
    // - When tour has it's own takeover
    return showingOfferBanner ||
      !!matchPath(currentPath, { path: homePageRoutes, exact: true }) ||
      !!matchPath(currentPath, { path: invalidExactRoutes, exact: true }) ||
      (!!exceptedRoutes.length && !!matchPath(currentPath, { path: exceptedRoutes })) ||
      !!matchPath(currentPath, { path: noBannerRoutes }) ||
      (!!siteTakeoverOnTour && !!matchPath(currentPath, { path: toursRoutes }))
  }, [currentPath, showingOfferBanner, siteTakeover, siteTakeoverOnTour])

  const onDismissHandler = useCallback(() => {
    setIsDismissed(true)
    // Dismiss for BANNER_DISMISSAL_PERIOD_DAYS days from now
    recordSiteBannerDismissal(popupIdentifier, moment().add(BANNER_DISMISSAL_PERIOD_DAYS, 'days').toISOString())
  }, [popupIdentifier])

  const isDesktop = useResponsiveSelector({
    mobile: false,
  }) ?? true
  const locationIcons = popupPayload?.locationIcons
  const logoConfig = useMemo<App.SiteTakeoverImageConfig | undefined>(() => {
    const locationIcon = locationIcons?.generic
    if (locationIcon) {
      const icon = isDesktop ? locationIcon.desktopIcon : locationIcon.mobileIcon
      if (icon) {
        const iconWidth = isDesktop ? locationIcon.desktopIconDimension?.width : locationIcon.mobileIconDimension?.width
        const iconHeight = isDesktop ? locationIcon.desktopIconDimension?.height : locationIcon.mobileIconDimension?.height
        return {
          id: icon,
          width: iconWidth,
          height: iconHeight,
        }
      }
    }
    const fallbackLogoRef = siteTakeoverTheme?.logoRef
    return fallbackLogoRef ? { id: fallbackLogoRef } : undefined
  }, [locationIcons, isDesktop, siteTakeoverTheme?.logoRef])

  if (isDismissed || hide) {
    return null
  }
  const backgroundStyle = popupPayload?.genericBannerType === 'whitepopup' ? 'white' : popupPayload?.backgroundStyle
  const title = popupPayload?.title ? stripMarkdownFormatting(popupPayload.title) : undefined
  const subtitle = popupPayload?.subtitle ? stripMarkdownFormatting(popupPayload.subtitle) : undefined
  const ctaLink = popupPayload?.ctaLink

  const isDismissible = popupPayload?.isDismissible
  const paletteType = popupPayload?.genericBannerType === 'whitepopup' ? 'default' : (siteTakeoverTheme?.palette ?? 'inverse')

  return (
    <PopupContainer className={getThemeClassName({ mobile: paletteType })}>
      <PopupBackground backgroundStyle={backgroundStyle} />
      <Content>
        <CSSBreakpoint max="mobile">
          <Group direction="horizontal" gap={8} verticalAlign="center" fullWidth>
            {logoConfig && <Image image={{ id: logoConfig.id }} dpr={2} enhancedFormats={false} width={logoConfig.width && logoConfig.height ? logoConfig.width : 32} height={logoConfig.width && logoConfig.height ? logoConfig.height : 32} />}
            <Group direction="vertical" gap={0} verticalAlign="start" grow>
              <BodyText variant="medium" weight="bold">{title}</BodyText>
              <Group direction="horizontal" gap={8} verticalAlign="center" wrap="wrap">
                <BodyText variant="small">{subtitle}</BodyText>
                {ctaLink && (
                  <TextLink to={ctaLink} weight="bold" size="small">View all</TextLink>
                )}
              </Group>
            </Group>
            {isDismissible && (
              <DismissButton onClick={onDismissHandler}>
                <LineMultiplyIcon size="S" />
              </DismissButton>
            )}
          </Group>
        </CSSBreakpoint>

        <CSSBreakpoint min="tablet">
          <Group direction="vertical" gap={8} verticalAlign="start" fullWidth>
            <Group direction="horizontal" gap={8} verticalAlign="start" fullWidth>
              {logoConfig && <Image id={logoConfig.id} dpr={2} enhancedFormats={false} width={logoConfig.width && logoConfig.height ? logoConfig.width : 48} height={logoConfig.width && logoConfig.height ? logoConfig.height : 48} />}
              <BodyText variant="large" weight="bold">{title}</BodyText>
              {isDismissible && (
                <IconButton
                  kind="tertiary"
                  variant="dark"
                  onClick={onDismissHandler}
                >
                  <LineMultiplyIcon size="S" />
                </IconButton>
              )}
            </Group>
            <Group direction="horizontal" gap={2} verticalAlign="center" wrap="wrap">
              <BodyText variant="medium">{subtitle}</BodyText>
              {ctaLink && (
                <TextLink to={ctaLink} weight="bold" size="medium">View all offers</TextLink>
              )}
            </Group>
          </Group>
        </CSSBreakpoint>
      </Content>
    </PopupContainer>
  )
}

export default SiteTakeoverPopup
